import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GalleryContext, Dimensions, NO_VALUE } from '../../consts';
import { GET_CREATIVE_TAGS } from '../../queries';
import { useBreakdownChartData, useCreativePerformanceData } from './hooks';
import { createCohortOptions, getCohortFieldAndPeriod } from '../../metricsUtils';
import { formatValue, getCreativeDisplayName } from '../../creativesFormattingUtils';
import {
    BREAKDOWN_DIMENSIONS,
    DIMENSION_OPTIONS,
    CHART_METRIC_KEYS,
    CHART_COHORT_METRICS,
    OVERVIEW_MULTI_VALUED_FIELDS,
    OVERVIEW_METRICS,
    OVERVIEW_COHORT_METRICS,
    HIDDEN_OVERVIEW_FIELDS,
} from './consts';

export const useCreativeTags = ({ app, imageHash }) => {
    const { creativesTags } = useContext(GalleryContext);

    const creativeFetchedTags = useMemo(
        () => creativesTags?.filter(creativeTag => creativeTag.imageHash === imageHash),
        [creativesTags, imageHash]
    );

    const { loading, data: creativeTagsResponse } = useQuery(GET_CREATIVE_TAGS, {
        variables: {
            appId: app,
            imageHashes: [imageHash],
        },
        skip: !!creativeFetchedTags?.length,
    });

    const aiDimensions = useMemo(() => creativeFetchedTags || creativeTagsResponse?.creativeTags?.tags || [], [
        creativeFetchedTags,
        creativeTagsResponse?.creativeTags?.tags,
    ]);

    return { loading, aiDimensions };
};

export const useCreativeAnalysis = ({ app, source, imageHash, selectedMetrics, selectedCohorts, dateRange }) => {
    const [selectedBreakdown, setSelectedBreakdown] = useState(DIMENSION_OPTIONS[0]);

    const { loading: performanceLoading, data: performanceChartData } = useCreativePerformanceData({
        app,
        source,
        imageHashes: [imageHash],
        metrics: selectedMetrics,
        cohortFields: selectedCohorts,
        dateRange,
    });

    const { loading: breakdownLoading, data: breakdownData } = useBreakdownChartData({
        app,
        source,
        imageHashes: [imageHash],
        metrics: selectedMetrics,
        cohortFields: selectedCohorts,
        dateRange,
        selectedBreakdown,
    });

    return {
        performance: {
            data: performanceChartData,
            loading: performanceLoading,
        },
        breakdown: {
            data: breakdownData,
            loading: breakdownLoading,
            selectedBreakdown,
            setSelectedBreakdown,
        },
    };
};

const useSimilarCreativesOverview = ({ app, source, imageHashes = [], dateRange }) => {
    const DIMENSION_MIN_WIDTH = 180;
    const METRICS_MIN_WIDTH = 80;

    const { loading, data, metadata, error } = useCreativePerformanceData({
        app,
        source,
        imageHashes,
        dimensions: OVERVIEW_MULTI_VALUED_FIELDS.map(field => field.originalField),
        metrics: OVERVIEW_METRICS,
        cohortFields: OVERVIEW_COHORT_METRICS,
        dateRange,
        timeBreakdown: 'all',
    });

    const columnDefs = useMemo(() => {
        const cols = Object.entries(metadata)
            .filter(([field]) => !HIDDEN_OVERVIEW_FIELDS.has(field))
            .map(([field, fieldMeta]) => ({
                field,
                minWidth: fieldMeta.type === 'dimension' ? DIMENSION_MIN_WIDTH : METRICS_MIN_WIDTH,
                valueFormatter: params => formatValue(params.value),
                headerName: fieldMeta.display_name || '',
            }));

        if (!cols.length) return [];

        OVERVIEW_COHORT_METRICS.forEach(cohortField => {
            const [metricName, periodName] = getCohortFieldAndPeriod(cohortField.value);
            const periodColumn = metadata[metricName]?.columns?.find(col => col.name === periodName);

            cols.push({
                field: cohortField.value,
                minWidth: METRICS_MIN_WIDTH,
                valueFormatter: params => formatValue(params.value),
                headerName: periodColumn?.display_name || '',
            });
        });

        return cols;
    }, [metadata]);

    return { loading, data, columnDefs, error };
};

const SPEC_DIMENSIONS = [
    BREAKDOWN_DIMENSIONS.CREATIVE_ORIENTATION,
    BREAKDOWN_DIMENSIONS.CREATIVE_ASPECT_RATIO,
    BREAKDOWN_DIMENSIONS.CREATIVE_VIDEO_DURATION,
];

const buildMetricOptionsFromMetadata = (metricKeys, fieldsMetadata) => {
    return metricKeys
        .filter(key => fieldsMetadata[key])
        .map(key => ({
            value: key,
            label: fieldsMetadata[key].display_name || key,
            display_format: fieldsMetadata[key].display_format,
        }));
};

const buildCohortMetricOptionsFromMetadata = (cohortMetricConfigs, fieldsMetadata, cohortPeriods) => {
    if (!cohortPeriods?.length) return [];

    const cohortMetrics = cohortMetricConfigs.map(config => {
        const metricMeta = fieldsMetadata[config.name];
        return {
            name: config.name,
            display_name: metricMeta?.display_name || config.fallback_label || config.name,
            display_format: metricMeta?.display_format || config.display_format || { type: 'number' },
        };
    });

    return createCohortOptions(cohortMetrics, cohortPeriods);
};

export const useSimilarCreativeAnalysis = ({ app, source, clusterCreatives = [], dateRange }) => {
    const { dimensionsByCategory, cohortPeriods, fieldsMetadata } = useContext(GalleryContext);
    const [selectedSpecBreakdown, setSelectedSpecBreakdown] = useState(SPEC_DIMENSIONS[0]);
    const [selectedAiDimension, setSelectedAiDimension] = useState();
    const [selectedBreakdownDimension, setSelectedBreakdownDimension] = useState(DIMENSION_OPTIONS[0]);

    const baseMetrics = useMemo(() => buildMetricOptionsFromMetadata(CHART_METRIC_KEYS, fieldsMetadata), [
        fieldsMetadata,
    ]);

    const cohortMetrics = useMemo(
        () => buildCohortMetricOptionsFromMetadata(CHART_COHORT_METRICS, fieldsMetadata, cohortPeriods),
        [fieldsMetadata, cohortPeriods]
    );

    const chartMetrics = useMemo(() => [...baseMetrics, ...cohortMetrics], [baseMetrics, cohortMetrics]);

    const aiDimensionOptions = useMemo(() => {
        if (!dimensionsByCategory) return [];

        return Object.values(dimensionsByCategory)
            .flat()
            .filter(dimension => dimension.valueType === 'Single')
            .map(({ id, name }) => ({
                value: id,
                label: name,
                name: id,
                display_name: name,
            }));
    }, [dimensionsByCategory]);

    useEffect(() => {
        const firstAiOption = aiDimensionOptions[0];
        if (firstAiOption && !selectedAiDimension) setSelectedAiDimension(firstAiOption);
    }, [aiDimensionOptions, selectedAiDimension]);

    const allCreativeHashes = useMemo(() => clusterCreatives.map(c => c.imageHash), [clusterCreatives]);

    const { loading: isLoadingOverview, data: overviewData, columnDefs } = useSimilarCreativesOverview({
        app,
        source,
        imageHashes: allCreativeHashes,
        dateRange,
    });

    const { data: specChartData, loading: isLoadingSpecChart } = useBreakdownChartData({
        app,
        source,
        imageHashes: allCreativeHashes,
        metrics: baseMetrics,
        cohortFields: cohortMetrics,
        dateRange,
        selectedBreakdown: selectedSpecBreakdown,
    });

    const { loading: isLoadingScatterData, data: scatterRawData } = useCreativePerformanceData({
        app,
        source,
        imageHashes: allCreativeHashes,
        dimensions: [],
        metrics: baseMetrics,
        cohortFields: cohortMetrics,
        dateRange,
        timeBreakdown: 'all',
    });

    const scatterData = useMemo(() => {
        if (!scatterRawData?.length) return [];

        const creativesByHash = clusterCreatives.reduce((acc, creative) => {
            acc[creative.imageHash] = creative;
            return acc;
        }, {});

        return scatterRawData.map(row => {
            const processedRow = { ...row };
            chartMetrics.forEach(({ value }) => {
                if (!processedRow[value] || processedRow[value] === NO_VALUE) {
                    processedRow[value] = 0;
                }
            });
            processedRow.name = getCreativeDisplayName(creativesByHash[row.creative_image_hash]?.multiValuedDimensions);

            return processedRow;
        });
    }, [chartMetrics, scatterRawData, clusterCreatives]);

    const { data: tagChartData, loading: isLoadingTagChart } = useBreakdownChartData({
        app,
        source,
        imageHashes: allCreativeHashes,
        metrics: baseMetrics,
        cohortFields: cohortMetrics,
        dateRange,
        selectedBreakdown: selectedAiDimension,
        isAiDimension: true,
    });

    const { data: breakdownChartData, loading: isLoadingBreakdownChart } = useBreakdownChartData({
        app,
        source,
        imageHashes: allCreativeHashes,
        metrics: baseMetrics,
        cohortFields: cohortMetrics,
        dateRange,
        selectedBreakdown: selectedBreakdownDimension,
        additionalDimensions: [Dimensions.NORMALIZED_CREATIVE_NAME],
    });

    const specCreativeCountByCategory = useMemo(() => {
        if (!specChartData?.length || !selectedSpecBreakdown?.value) return {};

        const countMap = specChartData.reduce((acc, row) => {
            const category = row[selectedSpecBreakdown.value];
            if (!category) return acc;

            if (!acc[category]) acc[category] = new Set();

            acc[category].add(row[Dimensions.CREATIVE_IMAGE_HASH]);
            return acc;
        }, {});

        return Object.fromEntries(Object.entries(countMap).map(([key, set]) => [key, set.size]));
    }, [specChartData, selectedSpecBreakdown?.value]);

    const specAllSameValue = useMemo(() => {
        if (!specChartData?.length || !selectedSpecBreakdown?.value) return false;

        const uniqueValues = new Set(specChartData.map(row => row[selectedSpecBreakdown.value]));
        return uniqueValues.size === 1;
    }, [specChartData, selectedSpecBreakdown?.value]);

    return {
        overview: {
            data: overviewData,
            columnDefs,
            loading: isLoadingOverview,
        },
        specAnalysis: {
            data: specChartData,
            loading: isLoadingSpecChart,
            selectedBreakdown: selectedSpecBreakdown,
            setSelectedBreakdown: setSelectedSpecBreakdown,
            creativeCountByCategory: specCreativeCountByCategory,
            allSameValue: specAllSameValue,
        },
        scatterAnalysis: {
            data: scatterData,
            loading: isLoadingScatterData,
        },
        tagAnalysis: {
            data: tagChartData,
            loading: isLoadingTagChart,
            selectedBreakdown: selectedAiDimension,
            setSelectedBreakdown: setSelectedAiDimension,
        },
        breakdownAnalysis: {
            data: breakdownChartData,
            loading: isLoadingBreakdownChart,
            selectedBreakdown: selectedBreakdownDimension,
            setSelectedBreakdown: setSelectedBreakdownDimension,
        },
        aiDimensionOptions,
        chartMetrics,
        specDimensions: SPEC_DIMENSIONS,
    };
};
